import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  getDay, isToday, isBefore, startOfDay,
  subMonths, addMonths, addWeeks, parseISO
} from 'date-fns';
import Layout from '../components/Layout';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isFutureDay(date) {
  return !isBefore(startOfDay(date), startOfDay(new Date()));
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
}

function recurringLabel(entry) {
  if (!entry.recurring || entry.recurring === 'none') return '';
  const freq = entry.recurring === 'weekly' ? 'Weekly' : 'Monthly';
  const limit = entry.recurringLimit === 0
    ? ' (indefinite)'
    : ` (${entry.recurringLimit} occurrence${entry.recurringLimit !== 1 ? 's' : ''})`;
  return `${freq}${limit}`;
}

function expandRecurring(entry, monthStart, monthEnd) {
  if (!entry.recurring || entry.recurring === 'none') return [];
  const results = [];
  const baseDate = parseISO(entry.date);
  const limit = entry.recurringLimit || 0;
  const maxOccurrences = limit === 0 ? 500 : limit;
  let count = 0;
  while (count < maxOccurrences) {
    if (count > 0) {
      let current;
      if (entry.recurring === 'weekly') {
        current = addWeeks(baseDate, count);
      } else {
        const next = new Date(baseDate);
        next.setMonth(baseDate.getMonth() + count);
        current = next;
      }
      const dayStart = startOfDay(current);
      if (!isBefore(dayStart, startOfDay(monthStart)) && !isBefore(startOfDay(monthEnd), dayStart)) {
        results.push({
          ...entry,
          id: `${entry.id}_recur_${count}`,
          date: format(current, 'yyyy-MM-dd'),
          isRecurInstance: true,
        });
      }
      if (isBefore(startOfDay(monthEnd), dayStart) && count > 5) break;
    }
    count++;
  }
  return results;
}

export default function ReadOnlyCalendar() {
  const [viewDate, setViewDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendar');
  const [detailEntry, setDetailEntry] = useState(null);

  useEffect(() => {
    async function fetch() {
      try {
        const snap = await getDocs(query(collection(db, 'entries'), orderBy('date')));
        setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      setLoading(false);
    }
    fetch();
  }, []);

  function getMonthEntries() {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const monthKey = format(viewDate, 'yyyy-MM');
    let all = [];
    entries.forEach(entry => {
      if (entry.date.startsWith(monthKey)) all.push(entry);
      if (entry.recurring && entry.recurring !== 'none') {
        all = all.concat(expandRecurring(entry, monthStart, monthEnd));
      }
    });
    return all;
  }

  function getEntriesForDay(date) {
    const key = format(date, 'yyyy-MM-dd');
    return getMonthEntries().filter(e => e.date === key);
  }

  function openDetail(entry, e) {
    e.stopPropagation();
    setDetailEntry(entry);
  }

  // ── CALENDAR ──
  function renderCalendar() {
    const start = startOfMonth(viewDate);
    const end = endOfMonth(viewDate);
    const days = eachDayOfInterval({ start, end });
    const startPad = getDay(start);

    return (
      <div className="cal-grid">
        {DAYS_SHORT.map(d => <div key={d} className="cal-day-hdr">{d}</div>)}
        {Array.from({ length: startPad }, (_, i) => <div key={`p${i}`} className="cal-day empty" />)}
        {days.map(date => {
          const dayEntries = getEntriesForDay(date);
          const future = isFutureDay(date);
          const today = isToday(date);
          return (
            <div
              key={date.toISOString()}
              className={`cal-day${today ? ' today' : ''}${!future && !today ? ' past' : ''}`}
            >
              <div className="cal-day-num">
                {today ? <span className="today-badge">{format(date, 'd')}</span> : format(date, 'd')}
              </div>
              {dayEntries.map(entry => (
                <div
                  key={entry.id}
                  className={`cal-entry${future ? ' future' : ''}${entry.recurring && entry.recurring !== 'none' ? ' recurring-badge' : ''}`}
                  onClick={e => openDetail(entry, e)}
                  title="Click to view details"
                >
                  {entry.time ? `${formatTime(entry.time)} ` : ''}{entry.title}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // ── LIST ──
  function renderList() {
    const monthKey = format(viewDate, 'yyyy-MM');
    const monthEntries = getMonthEntries()
      .filter(e => e.date.startsWith(monthKey))
      .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''));

    if (monthEntries.length === 0) {
      return (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, textTransform: 'uppercase', color: 'var(--navy)', marginBottom: 8 }}>
            No entries for {format(viewDate, 'MMMM yyyy')}
          </div>
        </div>
      );
    }

    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {monthEntries.map(entry => {
          const entryDate = parseISO(entry.date);
          const future = isFutureDay(entryDate);
          return (
            <div
              key={entry.id}
              className="list-entry"
              style={{ cursor: 'pointer' }}
              onClick={e => openDetail(entry, e)}
            >
              <div className={`list-date-block${future ? ' future' : ''}`}>
                <div className="list-date-day">{format(entryDate, 'd')}</div>
                <div className="list-date-mon">{format(entryDate, 'MMM')}</div>
              </div>
              <div className="list-entry-body">
                <div className="list-entry-title">
                  {entry.title}
                  {entry.recurring && entry.recurring !== 'none' && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>↺ {recurringLabel(entry)}</span>
                  )}
                </div>
                <div className="list-entry-meta">
                  {entry.time && <span>🕐 {formatTime(entry.time)} &nbsp;·&nbsp; </span>}
                  {format(entryDate, 'EEEE, MMMM d, yyyy')}
                  {future && <span style={{ marginLeft: 8, background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase' }}>Upcoming</span>}
                </div>
                {entry.description && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{entry.description}</div>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>View →</div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── DETAIL POPUP ──
  function renderDetail() {
    const e = detailEntry;
    if (!e) return null;
    const entryDate = parseISO(e.date);
    const future = isFutureDay(entryDate);
    return (
      <div className="modal-overlay" onClick={ev => ev.target === ev.currentTarget && setDetailEntry(null)}>
        <div className="modal">
          <div className="modal-header" style={{ background: future ? 'var(--red)' : 'var(--navy)' }}>
            <h3>{future ? '🔴 Upcoming Service Call' : '✓ Service Call'}</h3>
            <button className="modal-close" onClick={() => setDetailEntry(null)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="detail-row">
              <span className="detail-label">Date</span>
              <span className="detail-value">{format(entryDate, 'EEEE, MMMM d, yyyy')}</span>
            </div>
            {e.time && (
              <div className="detail-row">
                <span className="detail-label">Time</span>
                <span className="detail-value">{formatTime(e.time)}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Service</span>
              <span className="detail-value" style={{ fontWeight: 600 }}>{e.title}</span>
            </div>
            {e.description && (
              <div className="detail-row">
                <span className="detail-label">Details</span>
                <span className="detail-value">{e.description}</span>
              </div>
            )}
            {e.recurring && e.recurring !== 'none' && (
              <div className="detail-row">
                <span className="detail-label">Schedule</span>
                <span className="detail-value">↺ {recurringLabel(e)}</span>
              </div>
            )}
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--gray-bg)', borderRadius: 4, fontSize: 12, color: 'var(--text-muted)' }}>
              Scheduled by American Laundry Distributors, Inc. &nbsp;·&nbsp; Questions? Call <a href="tel:2106615995" style={{ color: 'var(--navy)' }}>210.661.5995</a>
            </div>
            <div style={{ textAlign: 'right', marginTop: 14 }}>
              <button className="btn btn-navy btn-sm" onClick={() => setDetailEntry(null)}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout
      pageTitle="Preventative Maintenance Calendar"
      pageSubtitle={`Grand Hyatt San Antonio · ${format(viewDate, 'MMMM yyyy')} · View Only`}
    >
      <div className="alert alert-info mb-20">
        📅 <strong>Read-only view</strong> — Service calendar provided by American Laundry Distributors, Inc.
        Click any entry to view details. Contact <a href="tel:2106615995" style={{ color: 'var(--navy)' }}>210.661.5995</a> or <a href="mailto:info@a-ldi.com" style={{ color: 'var(--navy)' }}>info@a-ldi.com</a> with questions.
      </div>

      <div className="flex-between mb-20">
        <div className="flex gap-8">
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 3, overflow: 'hidden' }}>
            {['calendar', 'list'].map(mode => (
              <button
                key={mode}
                className="btn btn-sm"
                style={{ borderRadius: 0, background: viewMode === mode ? 'var(--navy)' : '#fff', color: viewMode === mode ? '#fff' : 'var(--text)', border: 'none', borderLeft: mode === 'list' ? '1px solid var(--border)' : 'none' }}
                onClick={() => setViewMode(mode)}
              >
                {mode === 'calendar' ? '📅 Calendar' : '☰ List'}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setViewDate(d => subMonths(d, 1))}>← Prev</button>
          <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 600, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'center' }}>
            {format(viewDate, 'MMMM yyyy')}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setViewDate(d => addMonths(d, 1))}>Next →</button>
          {format(viewDate, 'yyyy-MM') !== format(new Date(), 'yyyy-MM') && (
            <button className="btn btn-primary btn-sm" onClick={() => setViewDate(new Date())}>Today</button>
          )}
        </div>
      </div>

      {loading ? <div className="spinner" /> : viewMode === 'calendar' ? renderCalendar() : renderList()}
      {detailEntry && renderDetail()}
    </Layout>
  );
}
